import { SortingState } from '@tanstack/react-table';
import { MachineFilters } from '../../hooks/useMachineFilters';
import { MachineStatus } from '../../types/machines/machine.types';

/**
 * Configuration for a machine column.
 * This defines all available columns - visibility is controlled by the user via column settings.
 */
export interface MachineColumnConfig {
  /** Unique column identifier (matches accessor key or id) */
  id: string;
  /** Display header for the column */
  header: string;
  /** Accessor key for nested properties (e.g., 'address.city' maps to machine.address?.city) */
  accessorKey?: string;
  /** Whether this column is visible by default */
  defaultVisible: boolean;
  /** Whether the column can be hidden by the user */
  canHide: boolean;
  /** Whether sorting is enabled */
  sortable: boolean;
  /** Whether filtering is enabled for this column */
  filterable: boolean;
  /** Filter type: 'search' for text search, 'select' for multi-select dropdown */
  filterType?: 'search' | 'select';
  /** GraphQL filter key (for backend filtering) */
  graphFilterKey?: string;
  /** Filter key in useMachineFilters (maps to the filter state key) */
  filterKey?: keyof MachineFilters;
  /**
   * Text shown instead of an empty cell. Use when a blank value carries
   * meaning that should be stated rather than left for the reader to infer.
   */
  emptyText?: string;
  /** Column width */
  size: number;
  /** Minimum column width */
  minSize?: number;
  /** Whether the column is pinned (select, actions) */
  pinned?: 'left' | 'right';
  /** Whether resizing is enabled */
  resizable?: boolean;
  /** Cell type for special rendering */
  cellType?: 'text' | 'badge' | 'status' | 'custom';
  /** For badge type, maps values to variants */
  badgeVariants?: Record<
    string,
    'success' | 'warning' | 'error' | 'info' | 'neutral'
  >;
}

/**
 * All available machine columns configuration.
 * Columns marked with defaultVisible: false will be hidden initially but available in column picker.
 * Add new EDMS properties here to make them available throughout the app.
 */
export const MACHINE_COLUMN_CONFIGS: MachineColumnConfig[] = [
  // Core columns (always visible by default)
  {
    id: 'displayName',
    header: 'Display name',
    defaultVisible: true,
    canHide: false, // Core identifier, always shown
    sortable: true,
    filterable: true,
    filterType: 'search',
    graphFilterKey: 'displayName',
    filterKey: 'displayName',
    size: 300,
    minSize: 150,
    resizable: true,
  },
  // Shows CustomerDto.name from the EDMS customer record, verbatim
  {
    id: 'customerName',
    header: 'Customer name',
    defaultVisible: true,
    canHide: true,
    sortable: true,
    filterable: true,
    filterType: 'select',
    graphFilterKey: 'customerIds',
    filterKey: 'customers',
    size: 250,
    resizable: true,
  },
  {
    id: 'plantName',
    accessorKey: 'plant',
    header: 'Plant',
    defaultVisible: true,
    canHide: true,
    sortable: true,
    filterable: true,
    filterType: 'select',
    graphFilterKey: 'plantIds',
    filterKey: 'plants',
    size: 250,
    resizable: true,
  },
  {
    id: 'status',
    header: 'Machine state',
    defaultVisible: true,
    canHide: true,
    sortable: true,
    filterable: true,
    filterType: 'select',
    graphFilterKey: 'statuses',
    filterKey: 'statuses',
    // The minimum must fit the widest badge ("Running With Warning") - a
    // status badge that truncates is unreadable, so this column never shrinks
    // below it.
    size: 200,
    minSize: 200,
    // Colours come from getMachineStatusVariant, not from badgeVariants: a
    // stale status overrides them with neutral, which a static map cannot do.
    cellType: 'status',
  },

  // Raw EDMS MachineStateDto.state, alongside the derived Status column above.
  // Off by default: side by side with Status it reads as a contradiction
  // ("offline" next to "Running"), which is what made people think Pulse and
  // EDMS disagreed. The Status tooltip already names what EDMS reported, so
  // this column is for troubleshooting rather than everyday use.
  {
    id: 'edmsState',
    header: 'EDMS reported state',
    defaultVisible: false,
    canHide: true,
    sortable: false,
    filterable: false,
    size: 160,
    resizable: true,
    emptyText: 'No state from EDMS',
  },
  {
    id: 'family',
    header: 'Family',
    defaultVisible: true,
    canHide: true,
    sortable: true,
    filterable: true,
    filterType: 'select',
    graphFilterKey: 'familyIds',
    filterKey: 'families',
    size: 200,
    resizable: true,
  },

  // Machine identifiers (hidden by default)
  {
    id: 'machineId',
    header: 'Machine ID',
    defaultVisible: false,
    canHide: true,
    sortable: true,
    filterable: true,
    filterType: 'search',
    graphFilterKey: 'machineIdFilter',
    filterKey: 'machineIdFilter',
    size: 200,
    resizable: true,
  },
  {
    id: 'serial',
    header: 'Serial',
    defaultVisible: false,
    canHide: true,
    sortable: true,
    filterable: true,
    filterType: 'search',
    graphFilterKey: 'serial',
    filterKey: 'serial',
    size: 150,
    resizable: true,
  },
  {
    id: 'partNumber',
    header: 'Part Number',
    defaultVisible: false,
    canHide: true,
    sortable: true,
    filterable: true,
    filterType: 'search',
    graphFilterKey: 'partNumber',
    filterKey: 'partNumber',
    size: 150,
    resizable: true,
  },
  {
    id: 'soid',
    header: 'SOID',
    defaultVisible: false,
    canHide: true,
    sortable: true,
    filterable: true,
    filterType: 'search',
    graphFilterKey: 'soid',
    filterKey: 'soid',
    size: 150,
    resizable: true,
  },

  // Address columns (hidden by default)
  {
    id: 'countryCode',
    header: 'Country Code',
    defaultVisible: false,
    canHide: true,
    sortable: true,
    filterable: true,
    filterType: 'select',
    graphFilterKey: 'countryCodes',
    filterKey: 'countryCodes',
    size: 120,
    resizable: true,
  },
  {
    id: 'country',
    header: 'Country',
    defaultVisible: false,
    canHide: true,
    sortable: true,
    filterable: true,
    filterType: 'select',
    graphFilterKey: 'countries',
    filterKey: 'countries',
    size: 150,
    resizable: true,
  },
  {
    id: 'city',
    header: 'City',
    defaultVisible: false,
    canHide: true,
    sortable: true,
    filterable: true,
    filterType: 'select',
    graphFilterKey: 'cities',
    filterKey: 'cities',
    size: 150,
    resizable: true,
  },
  {
    id: 'state',
    header: 'State/Province',
    defaultVisible: false,
    canHide: true,
    sortable: true,
    filterable: true,
    filterType: 'select',
    graphFilterKey: 'states',
    filterKey: 'states',
    size: 150,
    resizable: true,
  },
  {
    id: 'zipCode',
    header: 'Zip Code',
    defaultVisible: false,
    canHide: true,
    sortable: true,
    filterable: true,
    filterType: 'search',
    graphFilterKey: 'zipCode',
    filterKey: 'zipCode',
    size: 120,
    resizable: true,
  },
  {
    id: 'address1',
    header: 'Address',
    defaultVisible: false,
    canHide: true,
    sortable: true,
    filterable: true,
    filterType: 'search',
    graphFilterKey: 'address1',
    filterKey: 'address1',
    size: 200,
    resizable: true,
  },

  // Edge device columns, all off by default. They describe the device EDMS
  // provisioned for a machine, not the machine, and a machines list that leads
  // with provisioning states and ED-guids reads as an infrastructure inventory.
  // Everything here stays one click away in the column picker.
  //
  // EDMS exposes two ids per edge device: the IoT edge device id (ED-guid),
  // which is what EDMS device management and MLOps use, and its own database
  // id. Both are available so the value copied out of Pulse matches the system
  // it is pasted into.
  {
    id: 'edgeDeviceStatus',
    header: 'Edge Device Provisioning',
    defaultVisible: false,
    canHide: true,
    sortable: true,
    filterable: true,
    filterType: 'select',
    graphFilterKey: 'edgeDeviceStatuses',
    filterKey: 'edgeDeviceStatuses',
    size: 170,
    resizable: true,
  },
  // Device connectivity: whether the edge device is talking to the IoT hub right
  // now. Off by default. It is an EDMS/infrastructure fact rather than a fact
  // about the machine, and its "Offline" reads as a contradiction next to a
  // machine whose own state is Running - the two describe different things, and
  // sometimes different moments. EDMS device management and the MLOps portal
  // both show it; here it stays one click away in the column picker.
  {
    id: 'edgeDeviceConnectionStatus',
    header: 'Edge Device Connection',
    defaultVisible: false,
    canHide: true,
    sortable: true,
    filterable: true,
    filterType: 'select',
    graphFilterKey: 'edgeDeviceConnectionStatuses',
    filterKey: 'edgeDeviceConnectionStatuses',
    cellType: 'badge',
    badgeVariants: {
      Online: 'success',
      Offline: 'neutral',
      Unknown: 'neutral',
      'No device': 'neutral',
    },
    size: 170,
    resizable: true,
  },
  {
    id: 'edgeDeviceGuid',
    header: 'Edge Device ID (IoT)',
    defaultVisible: false,
    canHide: true,
    sortable: true,
    filterable: true,
    filterType: 'search',
    graphFilterKey: 'edgeDeviceGuid',
    filterKey: 'edgeDeviceGuid',
    size: 300,
    resizable: true,
  },
  {
    id: 'edgeDeviceId',
    header: 'Edge Device DB ID',
    defaultVisible: false,
    canHide: true,
    sortable: true,
    filterable: true,
    filterType: 'search',
    graphFilterKey: 'edgeDeviceId',
    filterKey: 'edgeDeviceId',
    size: 180,
    resizable: true,
  },
  {
    id: 'edgeDeviceName',
    header: 'Edge Device',
    defaultVisible: false,
    canHide: true,
    sortable: true,
    filterable: true,
    filterType: 'search',
    graphFilterKey: 'edgeDeviceName',
    filterKey: 'edgeDeviceName',
    size: 180,
    resizable: true,
  },

  // Customer record fields (hidden by default). The main Customer column
  // shows the record's name (reliably filled) with a fallback to EDMS's
  // displayName; these expose the raw fields for whoever wants them.
  {
    id: 'customerDisplayName',
    header: 'Customer display name',
    defaultVisible: false,
    canHide: true,
    sortable: true,
    filterable: false,
    size: 250,
    resizable: true,
  },
  {
    id: 'customerIfsId',
    header: 'Customer IFS ID',
    defaultVisible: false,
    canHide: true,
    sortable: true,
    filterable: false,
    size: 150,
    resizable: true,
  },
  {
    id: 'customerRepresentative',
    header: 'Customer representative',
    defaultVisible: false,
    canHide: true,
    sortable: true,
    filterable: false,
    size: 180,
    resizable: true,
  },
  {
    id: 'customerCategory',
    header: 'Customer category',
    defaultVisible: false,
    canHide: true,
    sortable: true,
    filterable: false,
    size: 160,
    resizable: true,
  },

  // Customer ID (hidden, for advanced use)
  {
    id: 'customerId',
    header: 'Customer ID',
    defaultVisible: false,
    canHide: true,
    sortable: true,
    filterable: false,
    size: 150,
    resizable: true,
  },

  // Plant ID (hidden, for advanced use)
  {
    id: 'plantId',
    header: 'Plant ID',
    defaultVisible: false,
    canHide: true,
    sortable: true,
    filterable: false,
    size: 150,
    resizable: true,
  },
];

/**
 * Get the default column visibility state based on config
 * Includes ALL columns so the ManageColumnsDialog can properly reset
 */
export function getDefaultColumnVisibility(): Record<string, boolean> {
  return DEFAULT_COLUMN_VISIBILITY;
}

// Computed once: callers pass it as a hook dependency and a prop, so a fresh
// object per call would defeat their memoization
const DEFAULT_COLUMN_VISIBILITY: Record<string, boolean> = Object.freeze(
  Object.fromEntries(
    MACHINE_COLUMN_CONFIGS.map((config) => [config.id, config.defaultVisible])
  )
);

/**
 * Get column IDs that should be forced visible due to active filters or sorting.
 * This ensures users can see the values they're filtering/sorting by.
 */
export function getForceVisibleColumns(
  filters: MachineFilters,
  sorting: SortingState
): string[] {
  const forceVisible: string[] = [];

  // Check each column config
  for (const config of MACHINE_COLUMN_CONFIGS) {
    // If sorting is active on this column, force it visible
    if (sorting.some((s) => s.id === config.id)) {
      forceVisible.push(config.id);
      continue;
    }

    // If a filter is active on this column, force it visible
    if (config.filterKey) {
      const filterValue = filters[config.filterKey];
      const hasActiveFilter = Array.isArray(filterValue)
        ? filterValue.length > 0
        : typeof filterValue === 'string' && filterValue.length > 0;

      if (hasActiveFilter) {
        forceVisible.push(config.id);
      }
    }
  }

  return forceVisible;
}

/**
 * Compute effective column visibility by merging user preferences with forced-visible columns.
 * Columns with active filters or sorting are always shown.
 */
export function computeEffectiveColumnVisibility(
  userVisibility: Record<string, boolean>,
  filters: MachineFilters,
  sorting: SortingState
): Record<string, boolean> {
  const forceVisible = getForceVisibleColumns(filters, sorting);
  const effective = { ...userVisibility };

  // Force these columns to be visible
  for (const columnId of forceVisible) {
    effective[columnId] = true;
  }

  return effective;
}

/**
 * Get all filterable columns with their configuration
 */
export function getFilterableColumns(): MachineColumnConfig[] {
  return MACHINE_COLUMN_CONFIGS.filter((c) => c.filterable && c.graphFilterKey);
}

/**
 * Get column config by ID
 */
export function getColumnConfig(id: string): MachineColumnConfig | undefined {
  return MACHINE_COLUMN_CONFIGS.find((c) => c.id === id);
}

/**
 * Format status for display (replace underscores with spaces and capitalize)
 */
export function formatStatus(status: MachineStatus): string {
  return status
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Badge colour for a machine status, shared by the table, the mobile list and
 * the detail page so one status never appears in two colours.
 *
 * Note that a *stale* status has no colour of its own: the reading is only the
 * last one EDMS recorded, so the caller renders it neutral rather than as a
 * live green "Running". See createStatusCell in createMachineColumns.tsx.
 */
export function getMachineStatusVariant(
  status: MachineStatus
): 'success' | 'warning' | 'error' | 'info' | 'neutral' {
  switch (status) {
    case 'running':
    case 'ready':
      return 'success';
    case 'running_with_warning':
    case 'ready_with_warning':
      return 'warning';
    case 'busy':
      return 'info';
    case 'not_ready':
      return 'error';
    case 'offline':
    case 'unknown':
    default:
      return 'neutral';
  }
}
